import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, AlertCircle, Shield, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { setBearer, setCsrfMirror } from "@/lib/api";
import BrandedHeader from "@/components/BrandedHeader";

/**
 * Panel member magic-link landing + dashboard.
 * - Exchanges magic token for a scoped cookie session
 * - Lists completed candidates for the assigned job
 * - Lets panelist submit rating + recommendation + notes per candidate
 */
export default function PanelAuthPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("authenticating");
  const [session, setSession] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ rating: 3, recommendation: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function authenticate() {
      try {
        const resp = await api.post(`/panel/auth/${encodeURIComponent(token)}`);
        setSession(resp.data);
        if (resp.data.csrf_token) setCsrfMirror(resp.data.csrf_token);
        // Preview fallback bearer (optional — backend doesn't return one here, but safe)
        if (resp.data.token) setBearer(resp.data.token);
        setStatus("authenticated");
        await loadDashboard();
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid panel link");
        setStatus("error");
      }
    }
    if (token) authenticate();
  }, [token]);

  const loadDashboard = async () => {
    try {
      const r = await api.get("/panel/dashboard");
      setCandidates(r.data.candidates || []);
      setSession((prev) => ({ ...(prev || {}), job: r.data.job, panelist: r.data.panelist }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load dashboard");
    }
  };

  const openCandidate = (c) => {
    setSelected(c);
    setForm({ rating: 3, recommendation: "", notes: "" });
  };

  const submitFeedback = async () => {
    if (!form.recommendation) {
      toast.error("Please pick a recommendation");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/panel/feedback", {
        candidate_id: selected.id,
        rating: form.rating,
        recommendation: form.recommendation,
        notes: form.notes,
      });
      toast.success("Feedback submitted");
      setSelected(null);
      await loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "authenticating") {
    return (
      <div className="min-h-screen bg-gray-50">
        <BrandedHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="py-12 flex items-center justify-center gap-3 text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Verifying your invite&hellip;</span>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gray-50">
        <BrandedHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Card data-testid="panel-auth-error">
            <CardContent className="py-12">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-red-900">Access denied</div>
                  <div className="text-sm text-red-800 mt-1">{error}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandedHeader />
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="panel-dashboard">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>Panel Review — {session?.job?.title}</CardTitle>
                <CardDescription>
                  Reviewing as {session?.panelist?.email}. Session valid for 7 days.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Candidates to review ({candidates.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {candidates.length === 0 ? (
              <p className="text-sm text-gray-500">No candidates have completed assessment for this job yet.</p>
            ) : (
              <div className="divide-y">
                {candidates.map((c) => (
                  <div
                    key={c.id}
                    className="py-3 flex items-center justify-between"
                    data-testid={`panel-candidate-row-${c.id}`}
                  >
                    <div>
                      <div className="font-medium">{c.name || c.email}</div>
                      <div className="text-xs text-gray-500">
                        {c.email} · Score: {c.test_score ?? "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {c.feedback_submitted ? (
                        <Badge variant="outline" className="text-green-700 border-green-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Submitted
                        </Badge>
                      ) : null}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCandidate(c)}
                        data-testid={`panel-review-btn-${c.id}`}
                      >
                        {c.feedback_submitted ? "Update" : "Review"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {selected && (
          <Card className="mt-6" data-testid="panel-feedback-form">
            <CardHeader>
              <CardTitle className="text-base">Feedback for {selected.name || selected.email}</CardTitle>
              <CardDescription>Score your assessment on a 1–5 scale.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Rating (1 = poor · 5 = excellent)</Label>
                <div className="flex items-center gap-1 mt-2" data-testid="panel-rating-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      data-testid={`panel-rating-${n}`}
                      aria-label={`Rate ${n}`}
                    >
                      <Star
                        className={`w-7 h-7 ${n <= form.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Recommendation</Label>
                <RadioGroup
                  value={form.recommendation}
                  onValueChange={(v) => setForm({ ...form, recommendation: v })}
                  className="mt-2 space-y-1"
                  data-testid="panel-recommendation-group"
                >
                  {[
                    { value: "ADVANCE", label: "Advance to next round", color: "text-green-700" },
                    { value: "HOLD", label: "Hold for now", color: "text-yellow-700" },
                    { value: "REJECT", label: "Reject", color: "text-red-700" },
                  ].map((opt) => (
                    <div key={opt.value} className="flex items-center gap-2">
                      <RadioGroupItem value={opt.value} id={`rec-${opt.value}`} data-testid={`panel-rec-${opt.value.toLowerCase()}`} />
                      <Label htmlFor={`rec-${opt.value}`} className={`font-normal ${opt.color}`}>
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="panel-notes">Notes</Label>
                <Textarea
                  id="panel-notes"
                  data-testid="panel-notes-input"
                  placeholder="Strengths, concerns, follow-up questions…"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={submitFeedback}
                  disabled={submitting}
                  className="flex-1"
                  data-testid="panel-submit-feedback-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Submit Feedback
                </Button>
                <Button variant="outline" onClick={() => setSelected(null)} data-testid="panel-cancel-btn">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
